import React, { useState, useEffect } from "react";
import {
  ConstrainedDialog as Dialog,
  ConstrainedDialogContent as DialogContent,
  ConstrainedDialogDescription as DialogDescription,
  ConstrainedDialogFooter as DialogFooter,
  ConstrainedDialogHeader as DialogHeader,
  ConstrainedDialogTitle as DialogTitle,
} from "@/components/ui/constrained-dialog";
import {
  ConstrainedAlertDialog as AlertDialog,
  ConstrainedAlertDialogAction as AlertDialogAction,
  ConstrainedAlertDialogCancel as AlertDialogCancel,
  ConstrainedAlertDialogContent as AlertDialogContent,
  ConstrainedAlertDialogDescription as AlertDialogDescription,
  ConstrainedAlertDialogFooter as AlertDialogFooter,
  ConstrainedAlertDialogHeader as AlertDialogHeader,
  ConstrainedAlertDialogTitle as AlertDialogTitle,
} from "@/components/ui/constrained-alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Project } from "@/services/interviewService";
import { useTheme } from "@/contexts/ThemeContext";

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    projectId: string,
    projectData: {
      name: string;
      description: string;
      target_n?: number;
      max_budget?: number;
      status: "active" | "inactive" | "archived";
      client: string;
      start_date: string;
    }
  ) => void;
  project: Project | null;
  hasActiveProject: boolean;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  project,
  hasActiveProject = false,
}) => {
  const { effectiveTheme } = useTheme();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [targetInterviews, setTargetInterviews] = useState<number | "">("");
  const [maxBudget, setMaxBudget] = useState<number | "">("");
  const [status, setStatus] = useState<"active" | "inactive" | "archived">(
    "active"
  );
  const [client, setClient] = useState("");
  const [startDate, setStartDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActiveWarning, setShowActiveWarning] = useState(false);
  const [pendingProjectData, setPendingProjectData] = useState<any>(null);

  // Load project data when project changes
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || "");
      setTargetInterviews(project.target_n || "");
      setMaxBudget(project.max_budget || "");
      setStatus(project.status || "active");
      setClient(project.client || "");
      setStartDate(project.start_date ? project.start_date.split("T")[0] : "");
    }
  }, [project]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!project) return;

    const projectData = {
      name,
      description,
      target_n: targetInterviews !== "" ? Number(targetInterviews) : undefined,
      max_budget: maxBudget !== "" ? Number(maxBudget) : undefined,
      status,
      client,
      start_date: startDate,
    };

    // If trying to change to active when another project is active
    if (
      status === "active" &&
      project.status !== "active" &&
      hasActiveProject
    ) {
      setPendingProjectData(projectData);
      setShowActiveWarning(true);
      return;
    }

    submitProject(projectData);
  };

  const submitProject = (projectData) => {
    if (!project) return;

    setIsSubmitting(true);
    onSubmit(project.id, projectData);
    setIsSubmitting(false);
  };

  const handleActiveWarningClose = () => {
    setShowActiveWarning(false);
    setPendingProjectData(null);
  };

  const handleActiveWarningConfirm = () => {
    if (pendingProjectData) {
      submitProject({
        ...pendingProjectData,
        status: "inactive", // Set to inactive since they already have an active project
      });
    }
    setShowActiveWarning(false);
    setPendingProjectData(null);
  };

  if (!project) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>
              Make changes to your project details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Case Code
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="col-span-3"
                  placeholder="ABC1"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="col-span-3"
                  rows={3}
                  placeholder="Brief description of the project / industry. Scope of work, VDD, CDD, Strategy, etc"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="client" className="text-right">
                  Target Company
                </Label>
                <Input
                  id="client"
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  className="col-span-3"
                  placeholder="Target company (e.g., Acme Corp)"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="targetInterviews" className="text-right">
                  Target Interviews
                </Label>
                <Input
                  id="targetInterviews"
                  type="number"
                  min="1"
                  value={targetInterviews}
                  onChange={(e) =>
                    setTargetInterviews(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  className="col-span-3"
                  placeholder="e.g., 15"
                  required
                  style={{
                    colorScheme: effectiveTheme === "dark" ? "dark" : "auto",
                  }}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="maxBudget" className="text-right">
                  Max Budget
                </Label>
                <Input
                  id="maxBudget"
                  type="number"
                  step="500"
                  value={maxBudget}
                  onChange={(e) =>
                    setMaxBudget(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  className="col-span-3"
                  placeholder="e.g., 25000"
                  required
                  style={{
                    colorScheme: effectiveTheme === "dark" ? "dark" : "auto",
                  }}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="startDate" className="text-right">
                  Start Date
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right">
                  Status
                </Label>
                <div className="col-span-3 flex gap-2">
                  <Button
                    type="button"
                    variant={status === "active" ? "default" : "outline"}
                    onClick={() => setStatus("active")}
                    size="sm"
                  >
                    Active
                  </Button>
                  <Button
                    type="button"
                    variant={status === "inactive" ? "default" : "outline"}
                    onClick={() => setStatus("inactive")}
                    size="sm"
                  >
                    Inactive
                  </Button>
                  <Button
                    type="button"
                    variant={status === "archived" ? "default" : "outline"}
                    onClick={() => setStatus("archived")}
                    size="sm"
                  >
                    Archived
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <div className="flex w-full justify-between">
                <div></div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!name || !startDate || isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showActiveWarning} onOpenChange={setShowActiveWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Active Project Already Exists</AlertDialogTitle>
            <AlertDialogDescription>
              You already have an active project. You can only have one active
              project at a time. Would you like to set this project as inactive
              for now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleActiveWarningClose}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleActiveWarningConfirm}>
              Set as Inactive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditProjectModal;
